<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="title"
    no-paddings-y
    no-paddings-x
    header-shadow
    :loading="loading"
    min-body-height="500px"
    :dialog-width="isExtraObjectsType ? '80%' : '30%'"
    :is-valid="isFormValid && (isExtraObjectsPatternValid || !isExtraObjectsType)"
    @agree="handleAgree"
    :agree-text="`dialog.agree${edit ? 'Save' : 'Add'}`"
    :disagree-text="readOnly ? 'dialog.disagreeClose' : 'dialog.disagree'"
    :has-agree="!readOnly"
  >
    <template #body>
      <v-row no-gutters class="pl-7">
        <v-col cols="6" class="pr-5 extra-objects-border" v-if="isFetched && isExtraObjectsType && cableFetched">
          <v-form v-model="isExtraObjectsPatternValid" ref="pattern-form">
            <ZDMLanternPatternTreeview
              v-if="tabName === 'street_lamp'"
              ref="lantern-pattern-treeview"
              :allow-to-copy-levels="[4]"
              :pattern="extraObjects"
              :pole-type-features="poleTypeFeatures"
              :head-type-features="headTypeFeatures"
              :pick-arm-features="pickArmFeatures"
              :disable-edit="readOnly"
            />
            <ZDMCabinetPatternTreeview
              v-else-if="tabName === 'cabinet'"
              ref="cabinet-pattern-treeview"
              :pattern="extraObjects"
              :disable-edit="readOnly"
            />
          </v-form>
        </v-col>
        <v-col :cols="isExtraObjectsType ? '6' : '12'" class="pr-5" v-if="isFetched && cableFetched">
          <v-form v-model="isFormValid" ref="form">
            <feature-card-data :elements="formSchema" v-model="values" :is-editing-on="!readOnly" disableWidthLimit />
          </v-form>
        </v-col>
        <main-loader v-else />
      </v-row>
    </template>
  </main-large-dialog>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { range } from 'lodash';

import FeatureCardData from '@/components/FeatureCardData';
import MainLargeDialog from '@/components/MainLargeDialog';
import MainLoader from '@/components/MainLoader';
import ZDMLanternPatternTreeview from '@/components/ZDMLanternPatternTreeview';
import ZDMCabinetPatternTreeview from '@/components/ZDMCabinetPatternTreeview';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'ZdmEditorFeatureDialog',
  components: { FeatureCardData, MainLargeDialog, MainLoader, ZDMLanternPatternTreeview, ZDMCabinetPatternTreeview },
  mixins: [prepareAttributesFeatureCardMixin],
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    copy: {
      type: Object,
    },
    dataSource: {
      type: String,
      required: true,
    },
    edit: {
      type: Object,
    },
    layer: {
      type: Number,
      required: true,
    },
    geometry: {
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    sessionId: {
      type: Number,
      required: true,
    },
    tabName: {
      type: String,
      required: true,
    },
    tabSessionObjects: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    dataSourcesFeatures: get('layers/dataSourcesFeatures'),
    layers: get('layers/layers'),
    layersMetadata: get('layers/metadata'),
    zdmDataDataSources: get('admin/modulesMapping@zdm_data.datasources'),
    defaultAttributesValues: get('admin/modulesMapping@zdm_sessions.default_values'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    cableFetched() {
      return this.tabName !== 'cable' || !this.cableLoading;
    },
    isExtraObjectsType() {
      return ['street_lamp', 'cabinet'].includes(this.tabName);
    },
    title() {
      return this.readOnly
        ? this.$i18n.t(`sidebar.zdmEditor.preview${this.$_capitalize(this.tabName)}`)
        : this.$i18n.t(`sidebar.zdmEditor.${this.edit ? 'edit' : 'add'}${this.$_capitalize(this.tabName)}`);
    },

    poleTypeFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_pole'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
    pickArmFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_pick_arm'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
    headTypeFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_head'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
    skipCopyAttributes() {
      const skipCopyAttributesKeys = {
        street_lamp: ['cabinet_attribute_name', 'circuit_attribute_name', 'power_station_attribute_name'],
        cable: [
          'cabinet_attribute_name',
          'circuit_attribute_name',
          'power_station_attribute_name',
          'clamp_from_attribute_name',
          'clamp_to_attribute_name',
        ],
      };
      return Object.keys(skipCopyAttributesKeys).reduce((total, current) => {
        const keys = skipCopyAttributesKeys[current];
        const attrs = keys.map(key => this.zdmDataDataSources[current]?.[key] || null);
        return { ...total, [current]: attrs };
      }, {});
    },
  },
  data: vm => ({
    formSchema: [],
    formSchemaCircuits: {},
    cableSchemaWatch: null,
    cableLoading: false,
    isFetched: true,
    isFormValid: false,
    isExtraObjectsPatternValid: false,
    loading: false,
    values: {},
    copyValuesCopy: {},
    extraObjects: vm.prepareNewExtraObjects(),
    copyExtraObjects: vm.prepareNewExtraObjects(),
  }),
  methods: {
    addFeatureToSession: call('zdm_editor/addFeatureToSession'),
    editFeatureFromSession: call('zdm_editor/editFeatureFromSession'),
    getCableIntersections: call('zdm_editor/getCableIntersections'),
    getLayerFeature: call('layers/getLayerFeature'),
    async getCopyProperties(layerId, featureId, dataSource, treeMetadata) {
      if ((!layerId && layerId !== 0) || (!featureId && featureId !== 0) || !dataSource) {
        this.values = { ...(this.defaultAttributesValues?.[this.tabName] || {}) };
        this.extraObjects = this.prepareNewExtraObjects();
        this.$refs.form?.resetValidation();
        this.copyValuesCopy = JSON.parse(JSON.stringify(this.values));
        this.copyExtraObjects = this.prepareNewExtraObjects();
        return;
      }
      this.isFetched = false;
      try {
        let feature;
        if (layerId === this.tabName) {
          feature = this.tabSessionObjects['insert'].find(feature => {
            return feature.metadata.id === featureId;
          });
          if (this.isExtraObjectsType) {
            this.extraObjects = this.prepareExtraObjects(feature.metadata.extra_objects);
            this.copyExtraObjects = JSON.parse(JSON.stringify(this.extraObjects));
          }
          await new Promise(resolve => setTimeout(resolve, 1000));
        } else if (this.isExtraObjectsType) {
          feature = treeMetadata;
          this.extraObjects = this.prepareExtraObjects(treeMetadata);
          this.copyExtraObjects = JSON.parse(JSON.stringify(this.extraObjects));
        } else {
          feature = JSON.parse(
            JSON.stringify(await this.getLayerFeature({ layer_id: layerId, feature_id: featureId }))
          );
        }
        const idAttributeName = this.layersMetadata[this.dataSource].attributes_schema.id_name;
        const properties = this.getFlattenProperties(feature, idAttributeName);
        this.values = this.getValues(this.formSchema, properties, !this.readOnly);
        if (!this.readOnly) {
          this.skipCopyAttributes[this.tabName]?.forEach(attribute => {
            delete this.values[attribute];
          });
          if (this.defaultAttributesValues[this.tabName]) {
            this.values = { ...this.values, ...this.defaultAttributesValues[this.tabName] };
          }
        }
        this.$refs.form?.resetValidation();
        this.copyValuesCopy = JSON.parse(JSON.stringify(this.values));
        this.isFetched = true;
      } catch {
        this.isFetched = true;
      }
    },
    handleAgree() {
      if (this.edit) {
        this.handleEditFeatureToSession();
      } else {
        this.handleAddFeatureToSession();
      }
    },
    getExtraObjects(objects = this.extraObjects, geometry = this.geometry) {
      const mainObject = objects[0];
      if (this.tabName === 'street_lamp') {
        const extraObjects = mainObject.children.map(pole => {
          if (!pole?.value || (Array.isArray(pole.value) && !pole.value.every(v => !!v))) {
            this.$store.set('snackbar/PUSH_MESSAGE!', {
              message: this.$i18n.t('sidebar.zdmEditor.poleTypeRule'),
            });
            return false;
          }
          const poleCatalogAttributeName = this.zdmDataDataSources.pole.catalog_attribute_name;
          return {
            geometry,
            properties: { [poleCatalogAttributeName]: pole.value },
            pick_arm: pole.children.map((pickArm, i) => {
              if (!pickArm?.value || (Array.isArray(pickArm.value) && !pickArm.value.every(v => !!v))) {
                this.$store.set('snackbar/PUSH_MESSAGE!', {
                  message: this.$i18n.t('sidebar.zdmEditor.pickArmsTypesRule'),
                });
                return false;
              }
              const pickArmCatalogAttributeName = this.zdmDataDataSources.pick_arm.catalog_attribute_name;
              return {
                main: i === 0,
                geometry,
                properties: { [pickArmCatalogAttributeName]: pickArm.value, 'zdm_data_pick_arm.main': i === 0 },
                head: pickArm.children.map(head => {
                  if (!head?.value || (Array.isArray(head.value) && !head.value.every(v => !!v))) {
                    this.$store.set('snackbar/PUSH_MESSAGE!', {
                      message: this.$i18n.t('sidebar.zdmEditor.headTypesRule'),
                    });
                    return false;
                  }
                  const headCatalogAttributeName = this.zdmDataDataSources.head.catalog_attribute_name;
                  return { geometry, properties: { [headCatalogAttributeName]: head.value } };
                }),
              };
            }),
          };
        });
        const pole = extraObjects[0];
        if (!pole || (Array.isArray(pole) && !pole.every(v => !!v))) {
          return false;
        } else if (
          !pole.pick_arm.every(pickArm => {
            return !!pickArm && pickArm?.head.every(head => !!head);
          })
        ) {
          return false;
        }
        return { pole: extraObjects };
      } else if (this.tabName === 'cabinet') {
        const extraObjects = mainObject.children.map(circuit => {
          const circuitNumberAttributeName = this.zdmDataDataSources.circuit.display_attribute_name;
          return {
            properties: { [circuitNumberAttributeName]: circuit.value },
          };
        });
        return { circuits: extraObjects };
      }
    },
    async handleAddFeatureToSession() {
      const payload = {
        datasource: this.dataSource,
        geometry: this.geometry,
        properties: this.values,
        session_id: this.sessionId,
      };
      if (this.extraObjects) {
        payload.extra_objects = this.getExtraObjects(this.extraObjects);
        if (!payload.extra_objects) return;
      }
      try {
        this.loading = true;
        await this.addFeatureToSession(payload);
        this.computedIsVisible = false;
        this.loading = false;
      } catch {
        this.loading = false;
      }
    },
    async handleEditFeatureToSession() {
      const data = {
        dataSource: this.dataSource,
        featureId: this.edit.metadata.id,
        sessionId: this.sessionId,
        payload: {
          properties: this.values,
        },
      };
      if (this.extraObjects) {
        data.payload.extra_objects = this.getExtraObjects(this.extraObjects, {
          ...this.edit.geometry,
          crs: { properties: { name: this.$_config.defaultEpsg }, type: 'name' },
        });
        if (!data.payload.extra_objects) return;
      }
      try {
        this.loading = true;
        await this.editFeatureFromSession(data);
        this.computedIsVisible = false;
        this.loading = false;
      } catch {
        this.loading = false;
      }
    },
    prepareExtraObjects(objects = this.edit?.extra_objects || {}) {
      if (this.tabName === 'street_lamp') {
        return [
          {
            name: this.$i18n.t('admin.street_lamp'),
            level: 1,
            children: objects.pole.map(pole => {
              const poleCatalogAttributeName = this.zdmDataDataSources.pole.catalog_attribute_name;
              return {
                name: this.$i18n.t('admin.pole'),
                selectItems: this.poleTypeFeatures,
                level: 2,
                value: pole.properties[poleCatalogAttributeName],
                children: pole.pick_arm.map((pickArm, pickArmIndex) => {
                  const pickArmCatalogAttributeName = this.zdmDataDataSources.pick_arm.catalog_attribute_name;
                  return {
                    name: this.$i18n.t(`admin.${pickArm.main ? 'pick_arm_primary' : 'pick_arm_secondary'}`),
                    index: pickArmIndex,
                    selectItems: this.pickArmFeatures,
                    level: 3,
                    value: pickArm.properties[pickArmCatalogAttributeName],
                    children: pickArm.head.map((headE, headIndex) => {
                      const headCatalogAttributeName = this.zdmDataDataSources.head.catalog_attribute_name;
                      return {
                        name: this.$i18n.t('admin.light_head'),
                        parentIndex: pickArmIndex,
                        index: headIndex,
                        selectItems: this.headTypeFeatures,
                        level: 4,
                        value: headE.properties[headCatalogAttributeName],
                      };
                    }),
                  };
                }),
              };
            }),
          },
        ];
      } else if (this.tabName === 'cabinet') {
        return [
          {
            name: this.$i18n.t('admin.cabinet'),
            level: 1,
            children: objects.circuits.map(circuit => {
              const circuitNumberAttributeName = this.zdmDataDataSources.circuit.display_attribute_name;
              return {
                name: this.$i18n.t('admin.circuit'),
                level: 2,
                value: circuit.properties[circuitNumberAttributeName],
              };
            }),
          },
        ];
      }
    },
    prepareNewExtraObjects(tabName = this.tabName) {
      if (tabName === 'street_lamp') {
        return [
          {
            name: this.$i18n.t('admin.street_lamp'),
            level: 1,
            children: [
              {
                name: this.$i18n.t('admin.pole'),
                selectItems: this.poleTypeFeatures,
                value: [undefined],
                level: 2,
                children: [
                  {
                    name: this.$i18n.t('admin.pick_arm_primary'),
                    selectItems: this.pickArmFeatures,
                    value: [undefined],
                    index: 0,
                    level: 3,
                    children: [
                      {
                        value: undefined,
                        index: 0,
                        level: 4,
                        name: this.$i18n.t('admin.light_head'),
                        parentIndex: 0,
                        selectItems: this.headTypeFeatures,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ];
      } else if (tabName === 'cabinet') {
        return [
          {
            name: this.$i18n.t('admin.cabinet'),
            level: 1,
            children: range(1, 9).map(value => {
              return { name: this.$i18n.t('admin.circuit'), value, level: 2 };
            }),
          },
        ];
      }
    },
    async setFormSchema(dataSource, layer, geometry) {
      let inters;
      let disabledAttributes = [];
      if (!this.readOnly && this.tabName === 'cable' && geometry) {
        this.cableLoading = true;
        try {
          inters = await this.getCableIntersections({ geometry, session_id: this.sessionId });
          if (!inters?.length) disabledAttributes = ['zdm_data_cable.cabinet_id', 'zdm_data_cable.circuit_id'];
          else
            this.formSchemaCircuits = inters.reduce((total, current) => {
              total[current.value] = current.circuits.map(circuit => {
                const { id, number } = circuit;
                return { value: id, text: number };
              });
              return total;
            }, {});
        } finally {
          this.cableLoading = false;
        }
      } else if (!this.readOnly && this.tabName === 'street_lamp') {
        disabledAttributes = ['zdm_data_street_lamp.cabinet_id', 'zdm_data_street_lamp.circuit_id'];
      }
      this.formSchema = this.getForm(
        this.layers[layer]?.form_schema.elements || {},
        this.layersMetadata[dataSource].attributes_schema.attributes,
        {
          predefinedItems:
            !this.readOnly && this.tabName === 'cable' && geometry
              ? { 'zdm_data_cable.cabinet_id': inters, 'zdm_data_cable.circuit_id': [] }
              : {},
          disabledAttributes,
          skipFilterExpressionRelation: true,
        }
      );
      if (this.tabName === 'cable' && !this.readOnly) this.setCircuitsFormSchema();
    },
    setCircuitsFormSchema(cabinetValue = this.values?.['zdm_data_cable.cabinet_id'] || null) {
      this.formSchema = this.formSchema.map(group => {
        return {
          ...group,
          elements: group.elements.map(attribute => {
            if (attribute.attribute === 'zdm_data_cable.circuit_id') {
              const items =
                cabinetValue && this.formSchemaCircuits[cabinetValue] ? this.formSchemaCircuits[cabinetValue] : [];
              return {
                ...attribute,
                items,
                rules: items?.length ? [v => v === '' || v === null || !!items.find(vv => vv.value === v) || ''] : [],
              };
            }
            return attribute;
          }),
        };
      });
      if (!cabinetValue && Object.prototype.hasOwnProperty.call(this.values, 'zdm_data_cable.circuit_id')) {
        this.values['zdm_data_cable.circuit_id'] = null;
      }
    },
  },
  watch: {
    computedIsVisible: {
      handler(nV) {
        if (nV) {
          this.$nextTick(() => {
            this.values = JSON.parse(JSON.stringify(this.edit ? this.edit.properties : this.copyValuesCopy));
            if (this.isExtraObjectsType) {
              this.extraObjects = JSON.parse(
                JSON.stringify(
                  this.edit?.metadata?.extra_objects
                    ? this.prepareExtraObjects(this.edit.metadata.extra_objects)
                    : this.copyExtraObjects
                )
              );
            } else {
              this.extraObjects = undefined;
            }
            this.$refs.form?.resetValidation();
            setTimeout(() => {
              this.$refs['lantern-pattern-treeview']?.$refs['lantern-pattern-treeview']?.updateAll(true);
              this.$refs['cabinet-pattern-treeview']?.$refs['cabinet-pattern-treeview']?.updateAll(true);
            });
          });
        } else {
          this.values = {};
          this.extraObjects = undefined;
        }
      },
      immediate: true,
    },
    tabName: {
      handler(nV) {
        if (nV === 'cable') {
          this.cableSchemaWatch = this.$watch(
            vm => {
              return vm.values?.['zdm_data_cable.cabinet_id'];
            },
            nV => {
              this.setCircuitsFormSchema(nV);
            },
            {
              immediate: true,
            }
          );
        } else if (this.cableSchemaWatch) {
          this.cableSchemaWatch();
        }
        this.extraObjects = this.prepareNewExtraObjects(nV);
        this.copyExtraObjects = this.prepareNewExtraObjects(nV);
      },
      immediate: true,
    },
  },
  mounted() {
    this.$watch(
      vm => {
        const { dataSource, layer, geometry, edit } = vm;
        return {
          dataSource,
          layer,
          geometry:
            geometry ||
            (this.edit?.geometry
              ? { ...edit.geometry, crs: { type: 'name', properties: { name: 'EPSG:3857' } } }
              : undefined),
        };
      },
      nV => {
        const { dataSource, layer, geometry } = nV;
        if (dataSource && (layer || layer === 0)) {
          this.setFormSchema(dataSource, layer, geometry);
        } else {
          this.formSchema = [];
          this.formSchemaCircuits = {};
        }
      },
      {
        immediate: true,
        deep: true,
      }
    );
    this.$watch(
      vm => {
        const { copy = {}, readOnly } = vm;
        const { id: featureId, layerId, dataSource, treeMetadata } = copy;
        return { featureId, layerId, dataSource, treeMetadata, readOnly };
      },
      nV => {
        const { featureId, layerId, dataSource, treeMetadata } = nV;
        this.getCopyProperties(layerId, featureId, dataSource, treeMetadata);
      },
      {
        immediate: true,
        deep: true,
      }
    );
  },
};
</script>
<style lang="scss">
.extra-objects-border {
  @include border(right, 1px);
}
</style>
